import { lazy, ReactElement } from 'react';

import { PERMISSIONS } from 'constants/Permissions';

const PatientList = lazy(() => import('pages/PatientList/PatientList'));
const PatientPage = lazy(() => import('pages/PatientPage/PatientPage'));
const Dashboard = lazy(() => import('pages/Dashboard/Dashboard'));
const Referrals = lazy(() => import('pages/Referrals/Referrals'));
const ReferPatient = lazy(() => import('pages/ReferPatient/ReferPatient'));
const Calendar = lazy(() => import('pages/Calendar/Calendar'));
const FinanceReport = lazy(() => import('pages/FinanceReport/FinanceReport'));
const CaseLibrary = lazy(() => import('pages/CaseLibrary/CaseLibrary'));
const CaseLibraryDetails = lazy(
  () => import('pages/CaseLibrary/CaseLibraryDetails')
);
const Settings = lazy(() => import('pages/Settings/Settings'));
const PageNotFound = lazy(() => import('pages/PageNotFound'));
const Inbox = lazy(() => import('pages/Inbox/Inbox'));
const NewChat = lazy(() => import('pages/NewChat/NewChat'));
const GeneralSettings = lazy(
  () => import('pages/GeneralSettings/GeneralSettings')
);
const BranchSettings = lazy(
  () => import('pages/BranchSettings/BranchSettings')
);
const UserSettings = lazy(() => import('pages/UserSettings/UserSettings'));
const LoginAndSecuritySettings = lazy(
  () => import('pages/LoginAndSecuritySettings/LoginAndSecuritySettings')
);
const ProfileSettings = lazy(
  () => import('pages/ProfileSettings/ProfileSettings')
);
const AppointmentListPage = lazy(
  () => import('pages/AppointmentListPage/index')
);
const AppointmentPage = lazy(
  () => import('pages/AppointmentPage/AppointmentPage')
);
const AppointmentSettings = lazy(
  () => import('pages/AppointmentSettings/AppointmentSettings')
);

const TPV = lazy(() => import('pages/TPV/TPV'));
const TreatmentPlanningPreference = lazy(
  () => import('pages/TreatmentPlanningPreference/TreatmentPlanningPreference')
);
const NotificationPreferences = lazy(
  () => import('pages/NotificationPreferences/NotificationPreferences')
);
const GiveInstructionsDetails = lazy(
  () => import('pages/GiveInstructionsDetails/GiveInstructionsDetails')
);
const TpvPreview = lazy(() => import('pages/TPVPreview/TPVPreview'));
const Salesforce = lazy(() => import('pages/Salesforce/Salesforce'));
const SalesforceLeads = lazy(
  () => import('pages/Salesforce/Leads/SalesforceLeads')
);
const SalesforceLeadDetail = lazy(
  () => import('pages/Salesforce/Leads/SalesforceLeadDetail')
);
const SalesforceAccounts = lazy(
  () => import('pages/Salesforce/Accounts/SalesforceAccounts')
);
const SalesforceAccountDetail = lazy(
  () => import('pages/Salesforce/Accounts/SalesforceAccountDetail')
);
const SalesforceOpportunities = lazy(
  () => import('pages/Salesforce/Opportunities/SalesforceOpportunities')
);
const SalesforceOpportunityDetail = lazy(
  () => import('pages/Salesforce/Opportunities/SalesforceOpportunityDetail')
);
const SalesforceEventDetail = lazy(
  () => import('pages/Salesforce/Events/SalesforceEventDetail')
);
const SalesforceTaskDetail = lazy(
  () => import('pages/Salesforce/Tasks/SalesforceTaskDetail')
);
const SalesforceOpportunityTrackerDetail = lazy(
  () =>
    import(
      'pages/Salesforce/OpportunityTrackers/SalesforceOpportunityTrackerDetail'
    )
);
const SalesforceContacts = lazy(
  () => import('pages/Salesforce/Contacts/SalesforceContacts')
);
const SalesforceContactDetail = lazy(
  () => import('pages/Salesforce/Contacts/SalesforceContactDetail')
);
const SalesforceTreatmentPlans = lazy(
  () => import('pages/Salesforce/TreatmentPlans/SalesforceTreatmentPlans')
);
const SalesforceTreatmentPlanVersions = lazy(
  () =>
    import(
      'pages/Salesforce/TreatmentPlanVersions/SalesforceTreatmentPlanVersions'
    )
);
const SalesforceTreatmentPlanVersionDetail = lazy(
  () =>
    import(
      'pages/Salesforce/TreatmentPlanVersions/SalesforceTreatmentPlanVersionDetail'
    )
);
const SalesforceTreatmentPlanDetail = lazy(
  () => import('pages/Salesforce/TreatmentPlans/SalesforceTreatmentPlanDetail')
);
const SalesforceProductionDetail = lazy(
  () => import('pages/Salesforce/Productions/SalesforceProductionDetail')
);
const SalesforceRetainerPrescriptionDetail = lazy(
  () =>
    import(
      'pages/Salesforce/RetainerPrescriptions/SalesforceRetainerPrescriptionDetail'
    )
);
const SalesforceWhiteningPrescriptionDetail = lazy(
  () =>
    import(
      'pages/Salesforce/WhiteningPrescriptions/SalesforceWhiteningPrescriptionDetail'
    )
);
const SalesforceCases = lazy(
  () => import('pages/Salesforce/Cases/SalesforceCases')
);
const SalesforceCaseDetail = lazy(
  () => import('pages/Salesforce/Cases/SalesforceCaseDetail')
);
const SalesforceCaseTrackerDetail = lazy(
  () => import('pages/Salesforce/CaseTrackers/SalesforceCaseTrackerDetail')
);
const SalesforceDeliveryDetail = lazy(
  () => import('pages/Salesforce/Deliveries/SalesforceDeliveryDetail')
);
const SalesforceChatDetail = lazy(
  () => import('pages/Salesforce/ChatDetails/SalesforceChatDetail')
);
const SalesforceDashboards = lazy(
  () => import('pages/Salesforce/Dashboards/SalesforceDashboards')
);
const SalesforceDashboardFolders = lazy(
  () => import('pages/Salesforce/Dashboards/SalesforceDashboardFolders')
);
const SalesforceDashboardViewer = lazy(
  () => import('pages/Salesforce/Dashboards/SalesforceDashboardViewer')
);
const SalesforceReports = lazy(
  () => import('pages/Salesforce/Reports/SalesforceReports')
);
const SalesforceReportFolders = lazy(
  () => import('pages/Salesforce/Reports/SalesforceReportFolders')
);
const SalesforceReportViewer = lazy(
  () => import('pages/Salesforce/Reports/SalesforceReportViewer')
);

interface Page {
  title: string;
  path: string;
  Component: ReactElement;
  permission?: string;
  redirectTo?: string;
}

interface PagePaths {
  [path: string]: string;
}

export const pagePaths: PagePaths = {
  dashboard: '/dashboard',
  login: '/login',
  forgot: '/forgot',
  patientList: '/patient/list',
  patientPage: '/patient/list/:id',
  calendar: '/calendar',
  retainerPurchase: '/retainers-purchase',
  retainerThankyou: '/retainers-purchase/thankyou',
  settings: '/settings',
  general: '/settings/general',
  branches: '/settings/branches',
  users: '/settings/users',
  security: '/settings/security',
  treatmentPlanningPreference: '/settings/treatmentPlanningPreference',
  appointment: '/settings/appointment',
  help: '/help',
  inbox: '/inbox',
  newChat: '/inbox/newChat',
  referrals: '/referrals',
  referPatient: '/referrals/referPatient',
  forgotPassword: '/en/reset-password',
  setPassword: '/en/set-password',
  profileSettings: '/profile',
  appointmentList: '/calendar/appointment/list',
  logout: '/logout',
  appointmentPage: '/appointment/list/:id',
  tpv: '/tpv/:id',
  financeReport: '/finance-report',
  createNewAccount: '/createNewAccount',
  consentForm: '/consent-form',
  giveInstructions: '/give-instructions',
  giveInstructionsDetails: '/give-instructions/:id',
  clinicPage: '/clinics/:id',
  webLink: '/offboarding-survey',
  notificationPreferences: '/settings/notification-preferences',
  lineResponse: '/line-response',
  defaultPath: '/',
  tpvPreview: '/tpv-preview/:id',
  caseLibrary: '/settings/case-library',
  caseLibraryDetails: '/settings/case-library/:id',
  salesforce: '/salesforce',
  salesforceLeads: '/salesforce/leads',
  salesforceLeadDetail: '/salesforce/leads/:leadId',
  salesforceAccounts: '/salesforce/accounts',
  salesforceAccountDetail: '/salesforce/accounts/:accountId',
  salesforceOpportunities: '/salesforce/opportunity',
  salesforceOpportunityDetail: '/salesforce/opportunity/:oppId',
  salesforceEventDetail: '/salesforce/event/:eventId',
  salesforceTaskDetail: '/salesforce/task/:taskId',
  salesforceOpportunityTrackerDetail:
    '/salesforce/opportunity-tracker/:trackerId',
  salesforceContacts: '/salesforce/contacts',
  salesforceContactDetail: '/salesforce/contacts/:contactId',
  salesforceProductionDetail: '/salesforce/production/:productionId',
  salesforceTreatmentPlans: '/salesforce/treatment-plans',
  salesforceTreatmentPlanDetail: '/salesforce/treatment-plans/:treatmentPlanId',
  salesforceTreatmentPlanVersions: '/salesforce/treatment-plan-versions',
  salesforceTreatmentPlanVersionDetail:
    '/salesforce/treatment-plan-versions/:treatmentPlanVersionId',
  salesforceRetainerPrescriptionDetail:
    '/salesforce/retainer-prescription/:retainerPrescriptionId',
  salesforceWhiteningPrescriptionDetail:
    '/salesforce/whitening-prescription/:whiteningPrescriptionId',
  salesforceCases: '/salesforce/cases',
  salesforceCaseDetail: '/salesforce/cases/:caseId',
  salesforceCaseTrackerDetail:
    '/salesforce/cases/:caseId/case-trackers/:caseTrackerId',
  salesforceDeliveryDetail: '/salesforce/delivery/:deliveryId',
  salesforceChatDetail: '/salesforce/chat-detail/:chatDetailId',
  salesforceDashboards: '/salesforce/dashboards',
  salesforceDashboardFolders: '/salesforce/dashboards/folders',
  salesforceDashboardViewer: '/salesforce/dashboards/:dashboardId',
  salesforceReports: '/salesforce/reports',
  salesforceReportFolders: '/salesforce/reports/folders',
  salesforceReportViewer: '/salesforce/reports/:reportId'
};

export const pages: Array<Page> = [
  {
    title: 'Dashboard',
    path: pagePaths.dashboard,
    Component: <Dashboard />,
    permission: PERMISSIONS.DASHBOARD_PAGE,
    redirectTo: pagePaths.patientList
  },
  {
    title: 'PatientList',
    path: pagePaths.patientList,
    Component: <PatientList />,
    permission: PERMISSIONS.PATIENT_LIST,
    redirectTo: pagePaths.financeReport
  },
  {
    title: 'GiveInstructionsDetails',
    path: pagePaths.giveInstructionsDetails,
    Component: <GiveInstructionsDetails /> //Todo :add permission
  },
  {
    title: 'PatientPage',
    path: pagePaths.patientPage,
    Component: <PatientPage />
  },
  {
    title: 'Referrals',
    path: pagePaths.referrals,
    Component: <Referrals />,
    permission: PERMISSIONS.REFERRAL
  },
  {
    title: 'ReferPatient',
    path: pagePaths.referPatient,
    Component: <ReferPatient />,
    permission: PERMISSIONS.CREATE_REFERRAL,
    redirectTo: pagePaths.referrals
  },
  {
    title: 'PageNotFound',
    path: '*',
    Component: <PageNotFound />
  },
  {
    title: 'Calendar',
    path: pagePaths.calendar,
    Component: <Calendar />,
    permission: PERMISSIONS.CALENDAR
  },
  {
    title: 'Case Library',
    path: pagePaths.caseLibrary,
    Component: <CaseLibrary />,
    permission: PERMISSIONS.CASE_LIBRARY
  },
  {
    title: 'Settings',
    path: pagePaths.settings,
    Component: <Settings />,
    permission: PERMISSIONS.SETTINGS
  },
  {
    title: 'General Settings',
    path: pagePaths.general,
    Component: <GeneralSettings />,
    permission: PERMISSIONS.GENERAL_SETTINGS
  },
  {
    title: 'Branch Settings',
    path: pagePaths.branches,
    Component: <BranchSettings />,
    permission: PERMISSIONS.BRANCH_SETTINGS
  },
  {
    title: 'Users Settings',
    path: pagePaths.users,
    Component: <UserSettings />,
    permission: PERMISSIONS.USER_SETTINGS
  },
  {
    title: 'Login and Security Settings',
    path: pagePaths.security,
    Component: <LoginAndSecuritySettings />
  },
  {
    title: 'Treatment Planning Preference',
    path: pagePaths.treatmentPlanningPreference,
    Component: <TreatmentPlanningPreference />,
    permission: PERMISSIONS.TREATMENT_PLANNING_PREFERENCE
  },
  {
    title: 'Appointment Preferences',
    path: pagePaths.appointment,
    Component: <AppointmentSettings />,
    permission: PERMISSIONS.USER_PREFERENCE_VIEW
  },
  {
    title: 'Dashboard',
    path: pagePaths.defaultPath,
    Component: <Dashboard />,
    permission: PERMISSIONS.DASHBOARD_PAGE,
    redirectTo: pagePaths.patientList
  },
  {
    title: 'New Chat',
    path: pagePaths.newChat,
    permission: PERMISSIONS.CREATE_TICKET,
    Component: <NewChat />
  },
  {
    title: 'Inbox',
    path: pagePaths.inbox,
    permission: PERMISSIONS.INBOX,
    Component: <Inbox />
  },
  {
    title: 'Profile',
    path: pagePaths.profileSettings,
    Component: <ProfileSettings />
  },
  {
    title: 'AppointmentList',
    path: pagePaths.appointmentList,
    Component: <AppointmentListPage />
  },
  {
    title: 'AppointmentPage',
    path: pagePaths.appointmentPage,
    Component: <AppointmentPage />
  },
  {
    title: 'Finance',
    path: pagePaths.financeReport,
    permission: PERMISSIONS.FINANCE_TAB_VIEW,
    Component: <FinanceReport />
  },
  {
    title: 'tpvPage',
    path: pagePaths.tpv,
    Component: <TPV />
  },
  {
    title: 'Notification Preferences',
    path: pagePaths.notificationPreferences,
    Component: <NotificationPreferences />,
    permission: PERMISSIONS.NOTIFICATION_PREFERENCE
  },
  {
    title: 'TPV Preview',
    path: pagePaths.tpvPreview,
    Component: <TpvPreview />
  },
  {
    title: 'Case Library Details',
    path: pagePaths.caseLibraryDetails,
    Component: <CaseLibraryDetails />
  },
  {
    title: 'Salesforce',
    path: pagePaths.salesforce,
    Component: <Salesforce />,
    permission: PERMISSIONS.SALESFORCE
  },
  {
    title: 'Salesforce Leads',
    path: pagePaths.salesforceLeads,
    Component: <SalesforceLeads />,
    permission: PERMISSIONS.SALESFORCE_LEADS_VIEW
  },
  {
    title: 'Salesforce Lead Detail',
    path: pagePaths.salesforceLeadDetail,
    Component: <SalesforceLeadDetail />,
    permission: PERMISSIONS.SALESFORCE_LEADS_VIEW
  },
  {
    title: 'Salesforce Accounts',
    path: pagePaths.salesforceAccounts,
    Component: <SalesforceAccounts />,
    permission: PERMISSIONS.SALESFORCE_ACCOUNTS_VIEW
  },
  {
    title: 'Salesforce Account Detail',
    path: pagePaths.salesforceAccountDetail,
    Component: <SalesforceAccountDetail />,
    permission: PERMISSIONS.SALESFORCE_ACCOUNTS_VIEW
  },
  {
    title: 'Salesforce Opportunities',
    path: pagePaths.salesforceOpportunities,
    Component: <SalesforceOpportunities />,
    permission: PERMISSIONS.SALESFORCE_OPPORTUNITIES_VIEW
  },
  {
    title: 'Salesforce Opportunity Detail',
    path: pagePaths.salesforceOpportunityDetail,
    Component: <SalesforceOpportunityDetail />,
    permission: PERMISSIONS.SALESFORCE_OPPORTUNITIES_VIEW
  },
  {
    title: 'Appointment Detail',
    path: pagePaths.salesforceEventDetail,
    Component: <SalesforceEventDetail />,
    permission: PERMISSIONS.SALESFORCE_EVENTS_VIEW
  },
  {
    title: 'Task Detail',
    path: pagePaths.salesforceTaskDetail,
    Component: <SalesforceTaskDetail />,
    permission: PERMISSIONS.SALESFORCE
  },
  {
    title: 'Opportunity Tracker Detail',
    path: pagePaths.salesforceOpportunityTrackerDetail,
    Component: <SalesforceOpportunityTrackerDetail />,
    permission: PERMISSIONS.SALESFORCE_OPPORTUNITIES_VIEW
  },
  {
    title: 'Salesforce Contacts',
    path: pagePaths.salesforceContacts,
    Component: <SalesforceContacts />,
    permission: PERMISSIONS.SALESFORCE_CONTACTS_VIEW
  },
  {
    title: 'Salesforce Contact Detail',
    path: pagePaths.salesforceContactDetail,
    Component: <SalesforceContactDetail />,
    permission: PERMISSIONS.SALESFORCE_CONTACTS_VIEW
  },
  {
    title: 'Salesforce Treatment Plans',
    path: pagePaths.salesforceTreatmentPlans,
    Component: <SalesforceTreatmentPlans />,
    permission: PERMISSIONS.SALESFORCE_TREATMENT_PLANS_VIEW
  },
  {
    title: 'Salesforce Treatment Plan Detail',
    path: pagePaths.salesforceTreatmentPlanDetail,
    Component: <SalesforceTreatmentPlanDetail />,
    permission: PERMISSIONS.SALESFORCE_TREATMENT_PLANS_VIEW
  },
  {
    title: 'Salesforce Treatment Plan Versions',
    path: pagePaths.salesforceTreatmentPlanVersions,
    Component: <SalesforceTreatmentPlanVersions />,
    permission: PERMISSIONS.SALESFORCE_TREATMENT_PLAN_VERSIONS_VIEW
  },
  {
    title: 'Salesforce Treatment Plan Version Detail',
    path: pagePaths.salesforceTreatmentPlanVersionDetail,
    Component: <SalesforceTreatmentPlanVersionDetail />,
    permission: PERMISSIONS.SALESFORCE_TREATMENT_PLAN_VERSIONS_VIEW
  },
  {
    title: 'Production Detail',
    path: pagePaths.salesforceProductionDetail,
    Component: <SalesforceProductionDetail />,
    permission: PERMISSIONS.SALESFORCE_PRODUCTIONS_VIEW
  },
  {
    title: 'Retainer Prescription Detail',
    path: pagePaths.salesforceRetainerPrescriptionDetail,
    Component: <SalesforceRetainerPrescriptionDetail />,
    permission: PERMISSIONS.SALESFORCE_RETAINER_PRESCRIPTIONS_VIEW
  },
  {
    title: 'Whitening Prescription Detail',
    path: pagePaths.salesforceWhiteningPrescriptionDetail,
    Component: <SalesforceWhiteningPrescriptionDetail />,
    permission: PERMISSIONS.SALESFORCE_WHITENING_PRESCRIPTIONS_VIEW
  },
  {
    title: 'Salesforce Cases',
    path: pagePaths.salesforceCases,
    Component: <SalesforceCases />,
    permission: PERMISSIONS.SALESFORCE_CASES_VIEW
  },
  {
    title: 'Salesforce Case Detail',
    path: pagePaths.salesforceCaseDetail,
    Component: <SalesforceCaseDetail />,
    permission: PERMISSIONS.SALESFORCE_CASES_VIEW
  },
  {
    title: 'Salesforce Case Tracker Detail',
    path: pagePaths.salesforceCaseTrackerDetail,
    Component: <SalesforceCaseTrackerDetail />,
    permission: PERMISSIONS.SALESFORCE_CASES_VIEW
  },
  {
    title: 'Delivery Detail',
    path: pagePaths.salesforceDeliveryDetail,
    Component: <SalesforceDeliveryDetail />,
    permission: PERMISSIONS.SALESFORCE_DELIVERIES_VIEW
  },
  {
    title: 'Chat Detail',
    path: pagePaths.salesforceChatDetail,
    Component: <SalesforceChatDetail />,
    permission: PERMISSIONS.SALESFORCE
  },
  {
    title: 'Salesforce Dashboards',
    path: pagePaths.salesforceDashboards,
    Component: <SalesforceDashboards />,
    permission: PERMISSIONS.SALESFORCE_REPORTS_VIEW
  },
  {
    title: 'Salesforce Dashboard Folders',
    path: pagePaths.salesforceDashboardFolders,
    Component: <SalesforceDashboardFolders />,
    permission: PERMISSIONS.SALESFORCE_REPORTS_VIEW
  },
  {
    title: 'Salesforce Dashboard Viewer',
    path: pagePaths.salesforceDashboardViewer,
    Component: <SalesforceDashboardViewer />,
    permission: PERMISSIONS.SALESFORCE_REPORTS_VIEW
  },
  {
    title: 'Salesforce Reports',
    path: pagePaths.salesforceReports,
    Component: <SalesforceReports />,
    permission: PERMISSIONS.SALESFORCE_REPORTS_VIEW
  },
  {
    title: 'Salesforce Report Folders',
    path: pagePaths.salesforceReportFolders,
    Component: <SalesforceReportFolders />,
    permission: PERMISSIONS.SALESFORCE_REPORTS_VIEW
  },
  {
    title: 'Salesforce Report Viewer',
    path: pagePaths.salesforceReportViewer,
    Component: <SalesforceReportViewer />,
    permission: PERMISSIONS.SALESFORCE_REPORTS_VIEW
  }
];
